/**
 * Forms opinión · configuración de la ficha de opinión.
 *
 * Ciclo borrador → publicada → cerrada. Al publicar, el backend genera un
 * formulario de Google POR grupo de interés y congela `preguntas_snapshot`:
 * editar una pregunta después NO altera el formulario ya publicado.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type ConfiguracionInstrumento from '@/interfaces/ConfiguracionInstrumento'
import type { PreviewConfiguracion, RespuestasFormulario, DestinatariosPayload, CalificarPayload,} from '@/interfaces/ConfiguracionInstrumento'
import type EvaluacionIndicador from '@/interfaces/EvaluacionIndicador'
import {
  crearConfiguracionService,
  listarConfiguracionesService,
  obtenerConfiguracionService,
  previewConfiguracionService,
  publicarConfiguracionService,
  asignarDestinatariosService,
  sincronizarService,
  obtenerRespuestasService,
  calificarService,
  cerrarConfiguracionService,
} from '@/services/ConfiguracionInstrumentoService'

/** Store `GestionConfiguracionInstrumento`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionConfiguracionInstrumento = defineStore('GestionConfiguracionInstrumento', {
  state: () => ({
    configuraciones: [] as ConfiguracionInstrumento[],
    configuracionActual: null as ConfiguracionInstrumento | null,
    preview: null as PreviewConfiguracion | null,
    respuestas: [] as RespuestasFormulario[],
    ultimaCalificacion: null as EvaluacionIndicador | null,
  }),

  actions: {
    async listar(params: {
      id_indicador?: number
      id_instrumento?: number
      estado?: 'borrador' | 'publicada' | 'cerrada'
    } = {}) {
      this.configuraciones = await listarConfiguracionesService(params)
    },

    async obtener(id: number, params: { id_ejecucion?: number; id_usuario?: number } = {}) {
      const result = await obtenerConfiguracionService(id, params)
      if (result.ok && result.data) this.configuracionActual = result.data
      return result
    },

    async crear(payload: {
      id_instrumento: number
      // Opcional: la configuración es una por FORMULARIO, y los indicadores que
      // cubre se derivan de sus preguntas.
      id_indicador?: number | null
      vigencia_desde?: string
      vigencia_hasta?: string
      titulo?: string
      instrucciones?: string
    }) {
      const result = await crearConfiguracionService(payload)
      if (result.ok && result.data) {
        this.configuraciones.push(result.data)
        this.configuracionActual = result.data
      }
      return result
    },

    async cargarPreview(id: number) {
      const result = await previewConfiguracionService(id)
      if (result.ok && result.data) this.preview = result.data
      return result
    },

    async publicar(id: number) {
      const result = await publicarConfiguracionService(id)
      if (result.ok && result.data) {
        this.configuracionActual = result.data
        this._actualizarEnLista(result.data)
      }
      return result
    },

    async asignarDestinatarios(id: number, destinatarios: DestinatariosPayload, idEjecucion?: number | null) {
      return asignarDestinatariosService(id, destinatarios, idEjecucion)
    },

    async sincronizar(id: number) {
      return sincronizarService(id)
    },

    async cargarRespuestas(id: number) {
      this.respuestas = await obtenerRespuestasService(id)
    },

    async calificar(id: number, payload: CalificarPayload) {
      const result = await calificarService(id, payload)
      if (result.ok && result.data) this.ultimaCalificacion = result.data
      return result
    },

    async cerrar(id: number) {
      const result = await cerrarConfiguracionService(id)
      if (result.ok && result.data) {
        this.configuracionActual = result.data
        this._actualizarEnLista(result.data)
      }
      return result
    },

    _actualizarEnLista(config: ConfiguracionInstrumento) {
      const idx = this.configuraciones.findIndex(c => c.id_configuracion === config.id_configuracion)
      if (idx !== -1) this.configuraciones[idx] = config
    },
  },
})
